
import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';

const StationSelection = ({ campaign, stationData, onClose }) => {
  const [selectedStations, setSelectedStations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter stations based on search
  const filteredStations = stationData.filter(station => 
    station.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    station.id.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Toggle station selection
  const toggleStation = (stationId) => {
    if (selectedStations.includes(stationId)) {
      setSelectedStations(selectedStations.filter(id => id !== stationId));
    } else {
      setSelectedStations([...selectedStations, stationId]);
    }
  };
  
  // Select all stations
  const selectAll = () => {
    setSelectedStations(stationData.map(station => station.id));
  };
  
  // Clear selection
  const clearSelection = () => {
    setSelectedStations([]);
  };
  
  // Implement campaign with selected stations
  const implementCampaign = () => {
    if (selectedStations.length === 0) {
      toast.error('Please select at least one station for the campaign');
      return;
    }
    
    toast.success(`Campaign "${campaign.name}" implemented to ${selectedStations.length} stations`);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Select Stations for Campaign</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 border-b border-gray-200 bg-purple-50">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <AlertCircle size={20} className="text-purple-600" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-purple-800">Implementing Campaign: {campaign?.name}</h4>
              <p className="mt-1 text-sm text-purple-700">{campaign?.description}</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 flex-grow overflow-auto">
          <div className="mb-4 flex items-center justify-between">
            <div className="relative rounded-md shadow-sm flex-grow mr-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md"
                placeholder="Search stations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex space-x-2">
              <button 
                className="px-3 py-1 text-xs font-medium text-purple-600 hover:text-purple-800"
                onClick={selectAll}
              >
                Select All
              </button>
              <button 
                className="px-3 py-1 text-xs font-medium text-purple-600 hover:text-purple-800"
                onClick={clearSelection}
              >
                Clear All
              </button>
            </div>
          </div>
          
          {/* Multi-selection tile grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredStations.length > 0 ? (
              filteredStations.map(station => {
                const isSelected = selectedStations.includes(station.id);
                
                return (
                  <div 
                    key={station.id}
                    className={`border rounded-md cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50 shadow-sm' 
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleStation(station.id)}
                  >
                    <div className="p-3 flex items-start">
                      <div className={`flex-shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center rounded-md mr-2 
                        ${isSelected ? 'bg-purple-600 text-white' : 'border border-gray-300'}`}
                      >
                        {isSelected && <Check size={12} />}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{station.name}</div>
                        <div className="text-xs text-gray-500">{station.id}</div>
                        <div className="mt-1 text-xs text-gray-600">
                          <span className="font-medium">Volume:</span> {station.volume?.toLocaleString() || 'N/A'} L/day
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 p-8 text-center text-gray-500">
                No stations match your search
              </div>
            )}
          </div>
        </div>
        
        <div className="border-t border-gray-200 p-4 flex justify-end space-x-3">
          <button 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
              selectedStations.length > 0 
                ? 'bg-purple-600 hover:bg-purple-700' 
                : 'bg-purple-400 cursor-not-allowed'
            }`}
            onClick={implementCampaign}
            disabled={selectedStations.length === 0}
          >
            Implement to {selectedStations.length} Station{selectedStations.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StationSelection;
